import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import api from '../../helpers/api';
import materioMultiSelectStyles from './materioSelectStyles';

/**
 * Multi-select picker for Work Orders (asset issues). Stores WO IDs in `value`.
 */
const WorkOrderMultiPicker = ({ value = [], onChange, organizationId, disabled }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  // Remembers every option we've ever fetched, so a selected work order stays
  // selected even after the options list is replaced by a new search.
  const optionCacheRef = useRef(new Map());

  // Friendly status labels — matches Reactive.jsx filter mapping.
  const statusLabel = (raw) => {
    if (!raw) return '';
    switch (String(raw).toUpperCase()) {
      case 'SUBMITTED': return t('shared.open', 'Open');
      case 'IN_PROGRESS': return t('shared.in_progress', 'In Progress');
      case 'RESOLVED': return t('shared.resolved', 'Resolved');
      case 'ON_HOLD': return t('shared.on_hold', 'On Hold');
      case 'PENDING': return t('shared.pending', 'Pending');
      default: return raw;
    }
  };

  const fetchWorkOrders = async (q = '') => {
    setLoading(true);
    try {
      const params = { count: 50 };
      if (organizationId) params.organizationId = organizationId;
      if (q && q.trim()) params.search = q.trim();
      const { data } = await api.get('asset-issues/organization/filtered', { params });
      const items = data?.response?.content || data?.response || [];
      setOptions(items.map((wo) => {
        const title = wo.title || wo.workOrderNumber || wo.assetIssueNumber || `#${wo.id}`;
        const status = statusLabel(wo.status);
        return {
          value: wo.id,
          label: status ? `${title} (${status})` : title,
          raw: wo,
        };
      }));
    } catch (e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const handleInput = (input) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchWorkOrders(input), 300);
  };

  const selected = useMemo(() => {
    // Merge the latest fetch into the cache, then resolve every selected id
    // against it — selections persist even when they're no longer in `options`.
    const cache = optionCacheRef.current;
    options.forEach((o) => cache.set(o.value, o));
    return (value || []).map((id) => cache.get(id) || { value: id, label: `#${id}` });
  }, [options, value]);

  return (
    <Select
      isMulti
      isDisabled={disabled}
      isLoading={loading}
      options={options}
      value={selected}
      onInputChange={handleInput}
      onChange={(opts) => onChange((opts || []).map((o) => o.value))}
      classNamePrefix="react-select"
      styles={materioMultiSelectStyles}
      placeholder={t('compliance.materiovigilance.field.linked_work_orders_placeholder', 'Search work orders…')}
      noOptionsMessage={() => loading
        ? t('shared.loading', 'Loading…')
        : t('compliance.materiovigilance.no_work_orders', 'No work orders found')}
      filterOption={null}
    />
  );
};

export default WorkOrderMultiPicker;
